import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import { 
  ArrowUp, 
  DollarSign, 
  ShoppingBag, 
  TrendingUp, 
  Calendar,
  CreditCard,
  CreditCard as CardIcon
} from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// StatCard Component
const StatCard = ({ title, value, subValue, icon: Icon }) => (
  <div className="bg-white rounded-lg shadow-md p-6 flex flex-col">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
      <Icon className="text-blue-500" size={20} />
    </div>
    <div className="flex flex-col">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      {subValue && (
        <span className="text-sm text-gray-500 mt-1">{subValue}</span>
      )}
    </div>
  </div>
);

// PaymentMethodCard Component
// Update the PaymentMethodCard component to receive totalRevenue and totalOrders as props
const PaymentMethodCard = ({ method, count, total, color, totalRevenue, totalOrders }) => {
  const percentage = ((count / total) * 100).toFixed(1);
  
  // Calculate estimated revenue based on the proportion of orders
  const averageOrderValue = totalRevenue / totalOrders;
  const estimatedRevenue = count * averageOrderValue;

  return (
    <div className="bg-gray-50 rounded-lg p-4 transition-all duration-300 hover:shadow-md">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CardIcon size={16} style={{ color }} />
          <span className="font-medium text-gray-700 text-sm">{method}</span>
        </div>
        <span className="text-xs font-medium text-gray-500">
          {percentage}%
        </span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className="h-2 rounded-full transition-all duration-500"
          style={{ 
            width: `${percentage}%`,
            backgroundColor: color
          }} 
        />
      </div>
      
      <div className="flex items-end justify-between mt-2">
        <div className="flex flex-col">
          <span className="text-2xl font-bold" style={{ color }}>
            {count}
          </span>
          <span className="text-xs text-gray-500">orders</span>
        </div>
        <div className="flex flex-col items-end">
        <span className="text-sm font-medium text-gray-600">
  EGP {estimatedRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
</span>
          <span className="text-xs text-gray-500">estimated</span>
        </div>
      </div>
    </div>
  );
};

const SalesStat = () => {
  const [stats, setStats] = useState({
    revenue: 0,
    profit: 0,
    totalSales: 0,
    bestSellingProduct: '',
    bestSellingProductSales: 0,
    bestMonth: '',
    bestMonthRevenue: 0,
    bestWeekDay: '',
    bestWeekDayRevenue: 0
  });
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [productCountChartData, setProductCountChartData] = useState({ labels: [], datasets: [] });
  const [productRevenueChartData, setProductRevenueChartData] = useState({ labels: [], datasets: [] });
  const [paymentMethodStats, setPaymentMethodStats] = useState({ labels: [], data: [] });
  const [topSalesDays, setTopSalesDays] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatMonthYear = (monthYear) => {
    const [month, year] = monthYear.split('/');
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'short', year: '2-digit' });
  };

  const generateRandomColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(`hsl(${Math.random() * 360}, 70%, 50%)`);
    }
    return colors;
  };

  const calculatePaymentMethodStats = (salesData) => {
    const paymentMethods = {
      'instaPay': 0,
      'Vfcash': 0,
      'Binance': 0,
      'Gate.io': 0,
      'Paypal': 0,
      'Cash': 0,
      'N/A': 0,
      'Other': 0
    };

    let totalRevenue = 0;
    let totalOrders = 0;

    salesData.forEach(sale => {
      if (sale.status === 'Completed') {
        const method = sale.paymentMethod || 'N/A';
        if (method in paymentMethods) {
          paymentMethods[method]++;
        } else {
          paymentMethods['Other']++;
        }
        totalOrders++;
        totalRevenue += parseFloat(sale.price) || 0;
      }
    });

    const colors = [
      '#4CAF50', // instaPay - Green
      '#2196F3', // Vfcash - Blue
      '#F4C430', // Binance - Yellow
      '#9C27B0', // Gate.io - Purple
      '#00BCD4', // Paypal - Cyan
      '#FF9800', // Cash - Orange
      '#9E9E9E', // N/A - Gray
      '#795548'  // Other - Brown
    ];

    setPaymentMethodStats({
      labels: Object.keys(paymentMethods),
      data: [{
        data: Object.values(paymentMethods),
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        totalRevenue,
        totalOrders
      }]
    });
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    const db = getFirestore();
    
    try {
      // Fetch sales data
      const salesSnapshot = await getDocs(collection(db, 'Sales'));
      const salesData = salesSnapshot.docs.map(doc => doc.data());
      
      // Fetch ads data
      const adsSnapshot = await getDocs(collection(db, 'Ads'));
      const adsData = adsSnapshot.docs.map(doc => doc.data());

      if (salesData.length === 0) {
        setError("No sales data available");
        return;
      }

      // Initialize variables for calculations
      let totalRevenue = 0;
      let totalSales = 0;
      let productSales = {};      // transaction count per product
      let productRevenue = {};    // total revenue (price×amount) per product
      let monthlyRevenue = {};
      let monthlyAds = {};
      let monthlyProfit = {};
      let dailyRevenue = {};

      // Process sales data
      salesData.forEach(sale => {
        if (sale.status === 'Completed') {
          const price = parseFloat(sale.price) || 0;
          const amount = parseInt(sale.amount) || 1;
          const revenue = price * amount;
          totalRevenue += revenue;
          totalSales += amount;

          if (sale.productName) {
            // Count of transactions (not units)
            productSales[sale.productName] = (productSales[sale.productName] || 0) + 1;
            // Total revenue per product
            productRevenue[sale.productName] = (productRevenue[sale.productName] || 0) + revenue;
          }

          const saleDate = new Date(sale.date);
          if (!isNaN(saleDate.getTime())) {
            const monthYear = `${saleDate.getMonth() + 1}/${saleDate.getFullYear()}`;
            monthlyRevenue[monthYear] = (monthlyRevenue[monthYear] || 0) + revenue;

            const dayKey = saleDate.toISOString().split('T')[0];
            dailyRevenue[dayKey] = (dailyRevenue[dayKey] || 0) + revenue;
          }
        }
      });

      // Calculate payment method stats
      calculatePaymentMethodStats(salesData);

      // Calculate weekly stats
      const bestDay = calculateWeeklyStats(salesData);

      // Process ads data
      adsData.forEach(ad => {
        const adDate = new Date(ad.date);
        const monthYear = `${adDate.getMonth() + 1}/${adDate.getFullYear()}`;
        monthlyAds[monthYear] = (monthlyAds[monthYear] || 0) + (parseFloat(ad.amount) || 0);
      });

      // Calculate monthly profits
      Object.keys(monthlyRevenue).forEach(month => {
        monthlyProfit[month] = (monthlyRevenue[month] || 0) - (monthlyAds[month] || 0);
      });

      const totalAdsCost = adsData.reduce((sum, ad) => sum + (parseFloat(ad.amount) || 0), 0);
      const bestSellingProduct = Object.keys(productSales).reduce((a, b) => 
        productSales[a] > productSales[b] ? a : b, ''
      );
      const bestMonth = Object.keys(monthlyRevenue).reduce((a, b) => 
        monthlyRevenue[a] > monthlyRevenue[b] ? a : b, ''
      );
      // Sort months chronologically
      const sortedMonths = Object.keys(monthlyRevenue).sort((a, b) => {
        const [aMonth, aYear] = a.split('/');
        const [bMonth, bYear] = b.split('/');
        return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
      });

      // Prepare chart data
      const labels = sortedMonths.map(formatMonthYear);
      setChartData({
        labels,
        datasets: [
          {
            label: 'Revenue',
            data: sortedMonths.map(month => monthlyRevenue[month] || 0),
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1
          },
          {
            label: 'Profit',
            data: sortedMonths.map(month => monthlyProfit[month] || 0),
            borderColor: 'rgb(255, 99, 132)',
            tension: 0.1
          },
          {
            label: 'Ads Spending',
            data: sortedMonths.map(month => monthlyAds[month] || 0),
            borderColor: 'rgb(255, 205, 86)',
            tension: 0.1
          }
        ]
      });

      // Pie chart — transaction count per product
      const productLabels = Object.keys(productSales);
      const backgroundColors = generateRandomColors(productLabels.length);
      setProductCountChartData({
        labels: productLabels,
        datasets: [{
          data: Object.values(productSales),
          backgroundColor: backgroundColors,
          hoverBackgroundColor: backgroundColors
        }]
      });

      // Pie chart — revenue (EGP) per product (same labels/colors for consistency)
      setProductRevenueChartData({
        labels: productLabels,
        datasets: [{
          data: productLabels.map(p => Math.round(productRevenue[p] || 0)),
          backgroundColor: backgroundColors,
          hoverBackgroundColor: backgroundColors
        }]
      });

      // Calculate top sales days
      const topDays = Object.entries(dailyRevenue)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([date, revenue]) => ({ date, revenue }));

      setTopSalesDays(topDays);

      // Update stats
      setStats({
        revenue: totalRevenue.toFixed(2),
        profit: (totalRevenue - totalAdsCost).toFixed(2),
        totalSales,
        bestSellingProduct,
        bestSellingProductSales: productSales[bestSellingProduct] || 0,
        bestMonth,
        bestMonthRevenue: monthlyRevenue[bestMonth]?.toFixed(2) || '0.00',
        bestWeekDay: bestDay.day,
        bestWeekDayRevenue: bestDay.revenue.toFixed(2)
      });

    } catch (error) {
      console.error("Error fetching stats:", error);
      setError("Failed to load sales statistics");
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklyStats = (salesData) => {
    const weeklyData = {
      Sunday: { revenue: 0, profit: 0, count: 0 },
      Monday: { revenue: 0, profit: 0, count: 0 },
      Tuesday: { revenue: 0, profit: 0, count: 0 },
      Wednesday: { revenue: 0, profit: 0, count: 0 },
      Thursday: { revenue: 0, profit: 0, count: 0 },
      Friday: { revenue: 0, profit: 0, count: 0 },
      Saturday: { revenue: 0, profit: 0, count: 0 }
    };

    salesData.forEach(sale => {
      if (sale.status === 'Completed' && sale.date) {
        const saleDate = new Date(sale.date);
        if (!isNaN(saleDate.getTime())) {
          const dayName = saleDate.toLocaleDateString('en-US', { weekday: 'long' });
          const price = parseFloat(sale.price) || 0;
          const amount = parseInt(sale.amount) || 1;
          const revenue = price * amount;
          
          if (weeklyData[dayName]) {
            weeklyData[dayName].revenue += revenue;
            weeklyData[dayName].count += amount;
            // Assuming profit margin of 30% for now (you can adjust this)
            weeklyData[dayName].profit += revenue * 0.3;
          }
        }
      }
    });

    // Find best day by revenue
    const bestDayEntry = Object.entries(weeklyData).reduce((max, [day, data]) => 
      data.revenue > max.revenue ? { day, revenue: data.revenue } : max, 
      { day: '', revenue: 0 }
    );

    // Return best day for stats
    return bestDayEntry;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Financial Performance',
        font: { size: 16, weight: 'bold' }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        align: 'center'
      },
      title: {
        display: true,
        text: 'Product Sales Distribution',
        font: { size: 16, weight: 'bold' }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        Error: {error}
      </div>
    );
  }
  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
        Sales Statistics
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
      <StatCard
  title="Revenue"
  value={`EGP ${stats.revenue}`}
  icon={DollarSign}
/>
<StatCard
  title="Profit"
  value={`EGP ${stats.profit}`}
  icon={TrendingUp}
/>
        <StatCard
          title="Total Sales"
          value={stats.totalSales}
          icon={ShoppingBag}
        />
        <StatCard
          title="Best Selling Product"
          value={stats.bestSellingProduct || 'N/A'}
          subValue={`Sales: ${stats.bestSellingProductSales}`}
          icon={ArrowUp}
        />
        <StatCard
  title="Best Month"
  value={stats.bestMonth || 'N/A'}
  subValue={`Revenue: EGP ${stats.bestMonthRevenue}`}
  icon={Calendar}
/>
        <StatCard
  title="Best Week Day"
  value={stats.bestWeekDay || 'N/A'}
  subValue={`Revenue: EGP ${stats.bestWeekDayRevenue}`}
  icon={Calendar}
/>
      </div>

      {/* Monthly Financial Performance — full width row */}
      <div className="mb-6">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="h-[350px] sm:h-[400px]">
            <Line options={chartOptions} data={chartData} />
          </div>
        </div>
      </div>

      {/* Two pie charts — side by side on tablet+, stacked on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="h-[350px] sm:h-[400px]">
            <Pie
              options={{
                ...pieChartOptions,
                plugins: {
                  ...pieChartOptions.plugins,
                  title: { display: true, text: 'Product Revenue Distribution (EGP)', font: { size: 15, weight: 'bold' } },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => ` EGP ${ctx.parsed.toLocaleString()} — ${ctx.label}`
                    }
                  }
                }
              }}
              data={productRevenueChartData}
            />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="h-[350px] sm:h-[400px]">
            <Pie
              options={{
                ...pieChartOptions,
                plugins: {
                  ...pieChartOptions.plugins,
                  title: { display: true, text: 'Product Sales Count (Transactions)', font: { size: 15, weight: 'bold' } },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => ` ${ctx.parsed} sales — ${ctx.label}`
                    }
                  }
                }
              }}
              data={productCountChartData}
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md sm:col-span-2">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <CreditCard className="text-blue-500" size={24} />
            Payment Methods Analysis
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
  {paymentMethodStats.labels.map((method, index) => (
    <PaymentMethodCard
      key={method}
      method={method}
      count={paymentMethodStats.data[0].data[index]}
      total={paymentMethodStats.data[0].totalOrders}
      color={paymentMethodStats.data[0].backgroundColor[index]}
      totalRevenue={paymentMethodStats.data[0].totalRevenue}
      totalOrders={paymentMethodStats.data[0].totalOrders}
    />
  ))}
</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Most Used Method</h4>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ 
                      backgroundColor: paymentMethodStats.data[0].backgroundColor[
                        paymentMethodStats.data[0].data.indexOf(
                          Math.max(...paymentMethodStats.data[0].data)
                        )
                      ]
                    }} 
                  />
                  <span className="font-medium">
                    {paymentMethodStats.labels[
                      paymentMethodStats.data[0].data.indexOf(
                        Math.max(...paymentMethodStats.data[0].data)
                      )
                    ]}
                  </span>
                </div>
                <span className="text-lg font-bold">
                  {Math.max(...paymentMethodStats.data[0].data)} orders
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Payment Diversity</h4>
              <div className="flex justify-between items-center">
                <span className="font-medium">Active Methods</span>
                <span className="text-lg font-bold">
                  {paymentMethodStats.labels.filter(
                    (_, i) => paymentMethodStats.data[0].data[i] > 0
                  ).length} of {paymentMethodStats.labels.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-xl font-semibold mb-4">Top 10 Sales Days</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topSalesDays.map((day, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {day.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
  EGP {day.revenue.toFixed(2)}
</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesStat;